// frontend/src/pages/CamerasPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-20
// Updated:      2026-09-25 12:03 BST
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Widget surface alignment fix (2026-09-25 12:03 BST):
//   - Removed the hard-coded five-widget Add Widget allow-list and component resolver.
//   - Add Widget now uses the canonical `cameras` surface from WidgetSurface / WidgetRegistry.
//   - Registry widgets assigned to Cameras, including ANPR Sighting Review, can now be selected.
//   - Stored widget self-heal now validates against the Cameras surface rather than a page-local id list.
//
// Description:  Cameras dashboard page for camera operations and live ANPR ingest surfaces.
//
// Mobile Responsiveness (2026-09-04):
//               - Added a dedicated mobile presentation at <= 768px.
//               - Mobile renders configured Camera widgets in a natural single-column flow.
//               - Mobile ordering follows the persisted desktop layout position (y, then x).
//               - react-grid-layout drag/resize behaviour remains desktop-only.
//               - Mobile rendering never persists layout changes, so viewing Cameras on a phone
//                 cannot alter the user's desktop widget arrangement.
//               - Reflowed the page heading and Add Widget action for compact mobile widths.
//               - Reduced mobile page gutters while preserving all existing Camera widgets,
//                 self-heal behaviour, localStorage keys, Add Widget flow, and desktop layout.
//
//               - Based on the JobsPage / MonitorPage layout standard.
//               - Intended for device-focused widgets only.
//               - Starts with Camera Enrollment, Camera Job Binding, Camera Heartbeat,
//                 Camera Ingest Activity, and Weaving Report as the initial live widgets.
//
// Notes:
//               - Uses its own localStorage keys so layout is isolated from other pages.
//               - Follows the canonical full-width page + react-grid-layout standard.
//               - Widget ids must stay aligned with WidgetRegistry / DB widget catalog.
//               - AddWidgetModal is wired and portalled at page level via the shared modal component.
//               - Uses Devices and Reporting categories so camera and weaving-report widgets can be grouped cleanly here.
//               - Includes self-heal for old placeholder widget ids previously stored in localStorage.

import React, { useState, useEffect, useCallback, useRef } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import WidgetWrapper from "../components/WidgetWrapper";
import AddWidgetModal from "../components/AddWidgetModal";
import {
  createSurfaceLayoutItem,
  getSurfaceWidget,
  isWidgetAvailableOnSurface,
} from "../widgets/WidgetSurface";

import { useTheme } from "../context/ThemeContext.jsx";

const SURFACE = "cameras";
const STORAGE_KEY_LAYOUT = "camerasLayout";
const STORAGE_KEY_WIDGETS = "camerasWidgets";
const MOBILE_BREAKPOINT = 768;
const LEGACY_REMOVED_WIDGET_IDS = ["cameras_page_placeholder"];

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const apply = () => {
      setIsMobile(mediaQuery.matches);
    };

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}
const defaultLayout = [
  { i: "camera_enrollment", x: 0, y: 0, w: 12, h: 16, minW: 8, minH: 10 },
  { i: "camera_heartbeat", x: 0, y: 16, w: 12, h: 11, minW: 8, minH: 8 },
  { i: "camera_job_binding", x: 0, y: 27, w: 12, h: 14, minW: 8, minH: 8 },
  { i: "camera_ingest_activity", x: 0, y: 41, w: 12, h: 12, minW: 8, minH: 8 },
  { i: "weaving_report_widget", x: 0, y: 53, w: 12, h: 12, minW: 8, minH: 8 },
];

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const CamerasPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [widgets, setWidgets] = useState({});
  const [layout, setLayout] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const gridRef = useRef(null);
  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;
  const [gridWidth, setGridWidth] = useState(1200);


  useEffect(() => {
    const element = gridRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setGridWidth(element.offsetWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const resolveWidgetComponent = (id) => {
    const match = getSurfaceWidget(SURFACE, id);
    return match?.component ? <match.component /> : null;
  };

  const resolveWidgetTitle = (id) =>
    getSurfaceWidget(SURFACE, id)?.friendly_name ||
    String(id || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  useEffect(() => {
    try {
      const storedLayout = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT));
      const storedWidgetData = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS));

      let isCorrupted = false;

      if (storedWidgetData && !Array.isArray(storedWidgetData)) {
        isCorrupted = true;
      }

      if (Array.isArray(storedWidgetData)) {
        if (storedWidgetData.some((item) => typeof item !== "string")) {
          isCorrupted = true;
        }
      }

      let sanitizedWidgetIds = Array.isArray(storedWidgetData) ? [...storedWidgetData] : [];
      let sanitizedLayout = Array.isArray(storedLayout) ? [...storedLayout] : [];

      if (!isCorrupted) {
        sanitizedWidgetIds = sanitizedWidgetIds.filter(
          (id) =>
            !LEGACY_REMOVED_WIDGET_IDS.includes(id) &&
            isWidgetAvailableOnSurface(SURFACE, id)
        );

        const validWidgetIdSet = new Set(sanitizedWidgetIds);
        sanitizedLayout = sanitizedLayout.filter(
          (item) =>
            item &&
            !LEGACY_REMOVED_WIDGET_IDS.includes(item.i) &&
            validWidgetIdSet.has(item.i)
        );
      }

      if (isCorrupted) {
        console.warn("Clearing corrupted CamerasPage localStorage...");
        localStorage.removeItem(STORAGE_KEY_LAYOUT);
        localStorage.removeItem(STORAGE_KEY_WIDGETS);
      }

      if (!isCorrupted && sanitizedWidgetIds.length > 0 && Array.isArray(sanitizedLayout)) {
        const normalised = normaliseLayout(sanitizedLayout);
        setLayout(normalised);

        const components = {};
        sanitizedWidgetIds.forEach((id) => {
          components[id] = resolveWidgetComponent(id);
        });

        setWidgets(components);

        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
        localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(sanitizedWidgetIds));

        console.log("✅ Restored widgets from storage:", sanitizedWidgetIds);
        return;
      }
    } catch (err) {
      console.error("Failed to load saved widget layout:", err);
    }

    const defaultWidgetIds = defaultLayout
      .map((item) => item.i)
      .filter((id) => isWidgetAvailableOnSurface(SURFACE, id));
    const defaultWidgetIdSet = new Set(defaultWidgetIds);
    const safeDefaultLayout = defaultLayout.filter((item) => defaultWidgetIdSet.has(item.i));
    const defaultWidgets = {};

    defaultWidgetIds.forEach((id) => {
      const component = resolveWidgetComponent(id);
      if (component) defaultWidgets[id] = component;
    });

    setLayout(safeDefaultLayout);
    setWidgets(defaultWidgets);

    try {
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(safeDefaultLayout));
      localStorage.setItem(
        STORAGE_KEY_WIDGETS,
        JSON.stringify(Object.keys(defaultWidgets))
      );
    } catch (err) {
      console.error("Failed to save default camera widget layout:", err);
    }

    console.log("✅ Loaded default widgets for CamerasPage");
  }, []);

  const handleAddWidget = (widgetId) => {
    if (!getSurfaceWidget(SURFACE, widgetId)) {
      alert("This widget is not available on the Cameras page.");
      return;
    }

    if (widgets[widgetId]) {
      alert("Widget already exists");
      return;
    }

    const component = resolveWidgetComponent(widgetId);
    if (!component) {
      alert("Unknown widget");
      return;
    }

    setWidgets((prev) => ({ ...prev, [widgetId]: component }));
    setLayout((prev) => {
      const bottomY = prev.length === 0 ? 0 : Math.max(...prev.map((l) => l.y + l.h));
      const newY = Math.max(0, bottomY);
      const newItem = createSurfaceLayoutItem(SURFACE, widgetId, {
        x: 0,
        y: newY,
        w: 6,
        h: 6,
        minW: 4,
        minH: 4,
        maxW: 12,
      });

      const newLayout = [
        ...prev,
        newItem,
      ];

      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
        localStorage.setItem(
          STORAGE_KEY_WIDGETS,
          JSON.stringify([...Object.keys({ ...widgets, [widgetId]: true })])
        );
      } catch (err) {
        console.error("Failed to save layout:", err);
      }

      return newLayout;
    });

    setShowAddModal(false);
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets((prev) => {
      const clone = { ...prev };
      delete clone[widgetId];
      return clone;
    });

    setLayout((prev) => {
      const filtered = prev.filter((item) => item.i !== widgetId);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(filtered));
        localStorage.setItem(
          STORAGE_KEY_WIDGETS,
          JSON.stringify(Object.keys(widgets).filter((id) => id !== widgetId))
        );
      } catch (err) {
        console.error("Failed to save layout:", err);
      }
      return filtered;
    });
  };

  const onLayoutChange = useCallback(
    (newLayout) => {
      const normalised = normaliseLayout(newLayout);
      setLayout(normalised);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
        localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets)));
      } catch (err) {
        console.error("Failed to save layout:", err);
      }
    },
    [widgets]
  );

  /*
   * Mobile is a presentation of the existing Cameras configuration, not a
   * second editable layout. Follow the saved desktop visual order without
   * persisting mobile positions back into camerasLayout.
   */
  const mobileWidgetEntries = Object.entries(widgets).sort(([a], [b]) => {
    const aIndex = layout.findIndex((item) => item.i === a);
    const bIndex = layout.findIndex((item) => item.i === b);

    const aLayout = aIndex >= 0 ? layout[aIndex] : null;
    const bLayout = bIndex >= 0 ? layout[bIndex] : null;

    const ay = Number.isFinite(aLayout?.y) ? aLayout.y : Number.MAX_SAFE_INTEGER;
    const by = Number.isFinite(bLayout?.y) ? bLayout.y : Number.MAX_SAFE_INTEGER;

    if (ay !== by) return ay - by;

    const ax = Number.isFinite(aLayout?.x) ? aLayout.x : Number.MAX_SAFE_INTEGER;
    const bx = Number.isFinite(bLayout?.x) ? bLayout.x : Number.MAX_SAFE_INTEGER;

    if (ax !== bx) return ax - bx;

    return aIndex - bIndex;
  });

  const getMobileWidgetHeight = (widgetId) => {
    const item = layout.find((layoutItem) => layoutItem.i === widgetId);

    const h = Number.isFinite(item?.h)
      ? Math.max(1, item.h)
      : 6;

    return Math.max(
      320,
      h * rowHeight + Math.max(0, h - 1) * 24
    );
  };

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingTop: isMobile ? "4px" : undefined,
        paddingBottom: isMobile ? "16px" : undefined,
        paddingLeft: isMobile ? "12px" : containerPadding,
        paddingRight: isMobile ? "12px" : containerPadding,
        boxSizing: "border-box",
      }}
    >
      <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            minHeight: isMobile ? "58px" : headerHeight,
            height: isMobile ? "auto" : headerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? "12px" : undefined,
            marginBottom: isMobile ? "6px" : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: 0,
              minWidth: 0,
            }}
          >
            Cameras
          </h2>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              backgroundColor: theme.button_action?.normal || "#000",
              color: "#fff",
              padding: isMobile ? "10px 14px" : "10px 18px",
              minHeight: isMobile ? "44px" : undefined,
              whiteSpace: "nowrap",
              borderRadius: "6px",
              fontWeight: "bold",
              fontFamily: "var(--font-body)",
              cursor: "pointer",
              border: "none",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.button_action?.hover || "#333";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.button_action?.normal || "#000";
            }}
            onMouseDown={(e) => {
              e.target.style.backgroundColor = theme.button_action?.click || "#111";
            }}
            onMouseUp={(e) => {
              e.target.style.backgroundColor = theme.button_action?.hover || "#333";
            }}
            title="Add Widget"
          >
            Add Widget
          </button>
        </div>

        {isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
              minWidth: 0,
              paddingBottom: "8px",
            }}
          >
            {mobileWidgetEntries.map(([key, WidgetComp]) => (
              <div
                key={key}
                style={{
                  width: "100%",
                  minWidth: 0,
                  height: `${getMobileWidgetHeight(key)}px`,
                  overflow: "hidden",
                }}
              >
                <WidgetWrapper
                  title={resolveWidgetTitle(key)}
                  onRemove={() => handleRemoveWidget(key)}
                >
                  {WidgetComp}
                </WidgetWrapper>
              </div>
            ))}
          </div>
        ) : (
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={40}
            width={gridWidth}
            draggableHandle=".widget-drag-handle"
            resizeHandles={["se"]}
            isResizable
            isDraggable
            onLayoutChange={onLayoutChange}
            compactType={null}
            preventCollision={false}
            margin={[16, 24]}
          >
            {Object.entries(widgets).map(([key, WidgetComp]) => (
              <div key={key}>
                <WidgetWrapper
                  title={resolveWidgetTitle(key)}
                  onRemove={() => handleRemoveWidget(key)}
                >
                  {WidgetComp}
                </WidgetWrapper>
              </div>
            ))}
          </GridLayout>
        )}

        {showAddModal && (
          <AddWidgetModal
            surface={SURFACE}
            excludedWidgetIds={Object.keys(widgets)}
            onClose={() => setShowAddModal(false)}
            onSelect={handleAddWidget}
          />
        )}
      </div>
    </div>
  );
};

export default CamerasPage;